<template>
  <div>
    <!-- Ride managers who have not signed the CURRENT business agreement version.
         Dismissable everywhere; the share page is the hard gate (EditRideAccessKey). -->
    <b-modal
      id="updatedTermsModal"
      v-model="isVisible"
      hide-header
      hide-footer
      centered
      scrollable
      size="lg"
      content-class="updated-terms-content"
    >
      <div class="updated-terms" :dir="isHebrew ? 'rtl' : 'ltr'">
        <h3 class="updated-terms-title">{{ t('updatedTermsTitle') }}</h3>
        <p class="updated-terms-text">{{ t('updatedTermsBody1') }}</p>
        <p class="updated-terms-text">{{ t('updatedTermsBody2') }}</p>

        <b-button variant="link" class="updated-terms-toggle p-0" @click="showTerms = !showTerms">
          {{ showTerms ? t('updatedTermsHideTerms') : t('updatedTermsReadTerms') }}
        </b-button>

        <div v-if="showTerms" class="updated-terms-doc" dir="auto">
          <h4 class="updated-terms-doc-title">{{ agreementDoc.docTitle }}</h4>
          <p>{{ agreementDoc.intro }}</p>
          <section v-for="(section, si) in agreementDoc.sections" :key="'sec-' + si">
            <h5 class="updated-terms-doc-section">{{ section.title }}</h5>
            <template v-for="(block, bi) in section.blocks">
              <p v-if="block.type === 'p'" :key="'p-' + si + '-' + bi">{{ block.text }}</p>
              <ul v-else-if="block.type === 'ul'" :key="'ul-' + si + '-' + bi">
                <li v-for="(item, ii) in block.items" :key="ii">{{ item }}</li>
              </ul>
              <div v-else-if="block.type === 'table'" :key="'tb-' + si + '-' + bi" class="updated-terms-table-wrap">
                <table class="updated-terms-table">
                  <thead>
                    <tr><th v-for="(h, hi) in block.headers" :key="hi">{{ h }}</th></tr>
                  </thead>
                  <tbody>
                    <tr v-for="(row, ri) in block.rows" :key="ri">
                      <td v-for="(cell, ci) in row" :key="ci">{{ cell }}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <div v-else-if="block.type === 'stack'" :key="'st-' + si + '-' + bi">
                <h6>{{ block.title }}</h6>
                <template v-for="(b2, bj) in block.blocks">
                  <p v-if="b2.type === 'p'" :key="'sb-p-' + si + '-' + bi + '-' + bj">{{ b2.text }}</p>
                  <ul v-else-if="b2.type === 'ul'" :key="'sb-ul-' + si + '-' + bi + '-' + bj">
                    <li v-for="(item, ii) in b2.items" :key="ii">{{ item }}</li>
                  </ul>
                </template>
              </div>
            </template>
          </section>
        </div>

        <!-- Only when no valid phone is on file; otherwise the server reuses it. -->
        <div v-if="!businessBasicAgreementHasPhone" class="updated-terms-phone">
          <label for="updated-terms-phone">{{ t('businessBasicAgreementPhoneLabel') }}</label>
          <input
            id="updated-terms-phone"
            v-model="phone"
            type="tel"
            inputmode="tel"
            autocomplete="tel"
            class="form-control"
            :disabled="isSubmitting"
            :placeholder="t('businessBasicAgreementPhonePlaceholder')"
            @blur="phoneBlurred = true"
          />
          <p v-if="phoneErrorText" class="updated-terms-error" role="alert">{{ phoneErrorText }}</p>
        </div>

        <label class="updated-terms-ack">
          <input v-model="accepted" type="checkbox" :disabled="isSubmitting" />
          <span>{{ t('updatedTermsCheckbox') }}</span>
        </label>
        <label class="updated-terms-ack">
          <input v-model="acceptedAi" type="checkbox" :disabled="isSubmitting" />
          <span>{{ t('businessBasicAgreementAiCheckbox') }}</span>
        </label>
        <p v-if="submitAttempted && !(accepted && acceptedAi)" class="updated-terms-error" role="alert">
          {{ t('businessBasicAgreementCheckboxRequired') }}
        </p>

        <div class="updated-terms-actions">
          <b-button variant="primary" :disabled="isSubmitting" @click="onAccept">
            <span v-if="isSubmitting" class="spinner-border spinner-border-sm" role="status" aria-hidden="true" />
            {{ t('updatedTermsAcceptButton') }}
          </b-button>
          <b-button variant="outline-secondary" :disabled="isSubmitting" @click="isVisible = false">
            {{ t('updatedTermsNotNow') }}
          </b-button>
        </div>
      </div>
    </b-modal>
  </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import { translationMixin } from "@/mixins/translationMixin";
import { getBusinessBasicAgreementByLanguage } from "@/content/saasBusinessBasicAgreementText";
import { isValidContactPhone } from "@/common/validateContactPhone";

export default {
  name: "UpdatedTermsModal",
  props: ["value"],
  mixins: [translationMixin],
  data() {
    return {
      accepted: false,
      acceptedAi: false,
      showTerms: false,
      phone: "",
      phoneBlurred: false,
      submitAttempted: false,
      isSubmitting: false,
    };
  },
  computed: {
    ...mapGetters(["businessBasicAgreementHasPhone"]),
    agreementDoc() {
      return getBusinessBasicAgreementByLanguage(this.isHebrew);
    },
    phoneErrorText() {
      if (this.businessBasicAgreementHasPhone) return "";
      if (!this.phoneBlurred && !this.submitAttempted) return "";
      const p = (this.phone || "").trim();
      if (!p) return this.t("businessBasicAgreementPhoneRequired");
      if (!isValidContactPhone(p)) return this.t("businessBasicAgreementPhoneInvalid");
      return "";
    },
    isVisible: {
      set(val) {
        this.$emit("input", val);
      },
      get() {
        return this.value;
      },
    },
  },
  watch: {
    isVisible(val) {
      if (val) {
        this.accepted = false;
        this.acceptedAi = false;
        this.showTerms = false;
        this.phone = "";
        this.phoneBlurred = false;
        this.submitAttempted = false;
        this.isSubmitting = false;
      }
    },
  },
  methods: {
    ...mapActions(["acceptUpdatedTerms"]),
    async onAccept() {
      if (this.isSubmitting) return;
      this.submitAttempted = true;
      if (!this.accepted || !this.acceptedAi || this.phoneErrorText) return;
      this.isSubmitting = true;
      try {
        await this.acceptUpdatedTerms({
          phone: this.businessBasicAgreementHasPhone ? "" : this.phone.trim(),
        });
      } finally {
        this.isSubmitting = false;
      }
    },
  },
};
</script>

<style scoped>
/* px, never rem, throughout: this app sets html { font-size: 10px }, so rem reads tiny. */
.updated-terms {
  padding: 8px 12px;
  text-align: start;
  font-size: 18px;
  line-height: 1.55;
}
.updated-terms-title {
  font-size: 28px;
  font-weight: 700;
  margin-bottom: 16px;
}
.updated-terms-text {
  font-size: 19px;
  margin-bottom: 12px;
}
.updated-terms-toggle,
.updated-terms-actions .btn {
  font-size: 18px;
}
.updated-terms-actions .btn {
  padding: 10px 24px;
}
.updated-terms-toggle {
  margin-bottom: 12px;
}
.updated-terms-doc {
  max-height: 40vh;
  overflow-y: auto;
  border: 1px solid #dee2e6;
  border-radius: 6px;
  padding: 12px 16px;
  margin-bottom: 16px;
  background: #fafafa;
  font-size: 16px;
}
.updated-terms-doc-title {
  font-size: 20px;
  font-weight: 700;
}
.updated-terms-doc-section {
  font-size: 17px;
  font-weight: 700;
  margin-top: 12px;
}
.updated-terms-table-wrap {
  overflow-x: auto;
  margin-bottom: 12px;
}
.updated-terms-table {
  width: 100%;
  border-collapse: collapse;
}
.updated-terms-table th,
.updated-terms-table td {
  border: 1px solid #dee2e6;
  padding: 5px 8px;
  text-align: center;
}
.updated-terms-phone {
  margin-bottom: 16px;
}
.updated-terms-phone .form-control {
  font-size: 17px;
  height: auto;
  padding: 8px 12px;
}
.updated-terms-ack {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  font-size: 18px;
  font-weight: 600;
  cursor: pointer;
}
.updated-terms-ack + .updated-terms-ack {
  margin-top: 10px;
}
.updated-terms-ack input {
  width: 20px;
  height: 20px;
  margin-top: 4px;
  flex-shrink: 0;
}
.updated-terms-error {
  color: #dc3545;
  font-size: 16px;
  margin: 4px 0 0;
}
.updated-terms-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-top: 20px;
}
</style>

<style>
/* Not scoped: the backdrop and .modal-content are rendered by b-modal, outside
   this component's scope. A light see-through backdrop instead of the app's solid
   black one (BootstrapVue 2.23 has no backdrop-variant), so the popup gets a shadow. */
#updatedTermsModal___BV_modal_outer_ .modal-backdrop {
  background-color: rgba(255, 255, 255, 0.55);
  opacity: 1;
}
.updated-terms-content {
  border: none;
  border-radius: 14px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.18);
}
</style>
